/* MijnRoosterPage — compacte weekview voor leidster, laat ALLEEN eigen shifts zien
   inclusief groep + locatie. Geen collega-info, geen roosterwijziging. */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarClock, ChevronLeft, ChevronRight, Clock, MapPin, Users2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { localISO, localToday, localWeekMonday } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import PushOptInCard from "@/components/PushOptInCard";

export default function MijnRoosterPage() {
  const { user } = useAuth();
  const [monday, setMonday] = useState(() => localWeekMonday(localToday()));
  const [shifts, setShifts] = useState([]);
  const [days, setDays] = useState([]);
  const [dl, setDl] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/rooster/mijn-week", { params: { week_start: monday } });
      setShifts(r.data.shifts || []);
      setDays(r.data.days || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [monday]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    const medId = user?.medewerker_id || user?.id;
    if (!medId) { toast.error("Medewerker-koppeling niet gevonden"); return; }
    setDl(true);
    try {
      const r = await api.get(`/rooster/pdf/medewerker/${medId}`, { params: { week_start: monday }, responseType: "blob" });
      // Trigger download via link
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rooster_${monday}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF gedownload");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF-download mislukt");
    } finally { setDl(false); }
  };

  const changeWeek = (delta) => {
    const d = new Date(monday + "T00:00:00");
    d.setDate(d.getDate() + delta * 7);
    setMonday(localISO(d));
  };

  const shiftsByDay = {};
  shifts.forEach(s => {
    shiftsByDay[s.datum] = shiftsByDay[s.datum] || [];
    shiftsByDay[s.datum].push(s);
  });

  return (
    <div>
      <PageHeader
        title="Mijn rooster"
        subtitle="Overzicht van je eigen shifts deze week — groep en locatie duidelijk zichtbaar."
        testid="mijn-rooster-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" size="icon" onClick={() => changeWeek(-1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Input type="date" value={monday} onChange={e => setMonday(localWeekMonday(e.target.value))} className="w-40" data-testid="mijn-week-date" />
            <Button variant="ghost" size="icon" onClick={() => changeWeek(1)} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={downloadPdf} disabled={dl || shifts.length === 0} data-testid="mijn-rooster-pdf" title="Download PDF om te printen of op de koelkast te plakken">
              <FileDown className="h-4 w-4 mr-1" /> {dl ? "Bezig…" : "PDF"}
            </Button>
          </div>
        }
      />

      <div className="mb-4 bg-white border border-neutral-200 rounded-md overflow-hidden max-w-2xl">
        <PushOptInCard />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {days.slice(0, 5).map(d => {
          const dt = new Date(d + "T00:00:00");
          const isToday = d === localISO(new Date());
          const wkNames = ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"];
          const wkFull = ["Zondag", "Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag"];
          const dayShifts = shiftsByDay[d] || [];
          return (
            <Card key={d} className={`p-4 bg-white shadow-none ${isToday ? "border-[color:var(--sage)] border-2" : ""}`} data-testid={`day-${d}`}>
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{wkFull[dt.getDay()]}</div>
                  <div className="font-display text-2xl text-neutral-800">{dt.getDate()}</div>
                  <div className="text-xs text-neutral-500">{dt.toLocaleDateString('nl-NL', { month: 'long' })}</div>
                </div>
                {isToday && <Badge variant="outline" className="text-[10px] bg-[color:var(--sage)]/10 text-[color:var(--sage)] border-[color:var(--sage)]/30">Vandaag</Badge>}
              </div>
              {dayShifts.length === 0 && (
                <div className="text-xs text-neutral-400 py-4 text-center italic">Geen shift ingepland</div>
              )}
              <div className="space-y-2">
                {dayShifts.map(s => (
                  <div key={s.id} className="p-2 rounded-md bg-[color:var(--sage)]/5 border border-[color:var(--sage)]/20" data-testid={`mijn-shift-${s.id}`}>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-neutral-800">
                      <Clock className="h-3.5 w-3.5 text-[color:var(--sage)]" />
                      {s.start} — {s.eind}
                    </div>
                    {s.group_name && (
                      <div className="flex items-center gap-1.5 text-xs text-neutral-600 mt-1">
                        <Users2 className="h-3 w-3" /> {s.group_name}
                      </div>
                    )}
                    {s.location_name && (
                      <div className="flex items-center gap-1.5 text-xs text-neutral-500 mt-0.5">
                        <MapPin className="h-3 w-3" /> {s.location_name}
                      </div>
                    )}
                    <div className="flex gap-1 mt-1.5">
                      {s.is_opener && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 h-4">Opener</Badge>}
                      {s.is_sluiter && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200 h-4">Sluiter</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      {shifts.length === 0 && (
        <div className="text-center py-12 text-sm text-neutral-500">
          <CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-30" />
          Nog geen rooster gepubliceerd voor deze week. Je krijgt automatisch een e-mail zodra de planner het rooster afrondt.
        </div>
      )}
    </div>
  );
}

